import { useState, useRef, useEffect } from "react";
import { useEditableImages } from "@/hooks/useEditableImages";
import { fileToDataUrl } from "@/hooks/imageStorage";
import { AnimTiltedCard } from '@readdy/anim/card/tilted-card/react';
import { AnimImageZoomRotate } from '@readdy/anim/card/image-zoom-rotate/react';
import { useIsMobile } from "@/hooks/useIsMobile";

const cardLabels: { key: keyof typeof defaultCardKeys; title: string }[] = [
  { key: "card1", title: "無料シャトル" },
  { key: "card2", title: "駐車スペース" },
  { key: "card3", title: "駐車管理スタッフ" },
];

const defaultCardKeys = {
  card1: "card1" as const,
  card2: "card2" as const,
  card3: "card3" as const,
};

function ImageEditPanel({
  open,
  onClose,
  images,
  updateImage,
  resetAll,
  hasCustom,
}: {
  open: boolean;
  onClose: () => void;
  images: ReturnType<typeof useEditableImages>["images"];
  updateImage: ReturnType<typeof useEditableImages>["updateImage"];
  resetAll: ReturnType<typeof useEditableImages>["resetAll"];
  hasCustom: ReturnType<typeof useEditableImages>["hasCustom"];
}) {
  const [localUrls, setLocalUrls] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<"hero" | "cards">("hero");
  const [uploading, setUploading] = useState<Record<string, boolean>>({});

  if (!open) return null;

  const fields =
    activeTab === "hero"
      ? [{ key: "heroBg" as const, label: "ヒーロー背景" }]
      : cardLabels;

  const handleApplyUrl = (key: string) => {
    const url = localUrls[key]?.trim();
    if (url) {
      updateImage(key as keyof typeof images, url);
      setLocalUrls((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleFileUpload = async (key: string, file: File) => {
    setUploading((prev) => ({ ...prev, [key]: true }));
    try {
      const dataUrl = await fileToDataUrl(file);
      await updateImage(key as keyof typeof images, dataUrl);
    } catch {
      // ignore
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const customCount = hasCustom() ? " (カスタムあり)" : "";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-heading text-lg text-foreground-950">
            画像を変更{customCount}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 cursor-pointer transition-colors"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="px-6 pt-4">
          <div className="flex gap-1 bg-background-100 rounded-full p-1">
            <button
              onClick={() => setActiveTab("hero")}
              className={`flex-1 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                activeTab === "hero"
                  ? "bg-background-50 text-foreground-950"
                  : "text-foreground-600 hover:text-foreground-800"
              }`}
            >
              ヒーロー背景
            </button>
            <button
              onClick={() => setActiveTab("cards")}
              className={`flex-1 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                activeTab === "cards"
                  ? "bg-background-50 text-foreground-950"
                  : "text-foreground-600 hover:text-foreground-800"
              }`}
            >
              カード画像
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {fields.map((f) => {
            const currentUrl = images[f.key];
            const inputValue =
              localUrls[f.key] !== undefined ? localUrls[f.key] : "";
            const isUploading = uploading[f.key];

            return (
              <div key={f.key}>
                <label className="block text-xs text-foreground-700 mb-1.5 font-medium">
                  {f.label}
                </label>
                <div className="relative w-full h-36 rounded-lg overflow-hidden mb-3 bg-background-100">
                  <img
                    src={currentUrl}
                    alt={f.label}
                    className="absolute inset-0 w-full h-full object-contain"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                  {isUploading && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <span className="text-background-50 text-sm flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        アップロード中...
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] text-foreground-400 whitespace-nowrap">
                    ファイルから選択
                  </span>
                  <div className="flex-1 h-px bg-background-200" />
                </div>

                <label className="flex items-center justify-center gap-2 w-full py-2.5 rounded-md border-2 border-dashed border-background-300 hover:border-primary-400 bg-background-50 hover:bg-background-100 cursor-pointer transition-colors mb-3">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(f.key, file);
                      e.target.value = "";
                    }}
                  />
                  <i className="ri-image-add-line text-foreground-600" />
                  <span className="text-sm text-foreground-600">
                    画像ファイルを選択
                  </span>
                </label>

                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] text-foreground-400 whitespace-nowrap">
                    URLで指定
                  </span>
                  <div className="flex-1 h-px bg-background-200" />
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) =>
                      setLocalUrls((prev) => ({
                        ...prev,
                        [f.key]: e.target.value,
                      }))
                    }
                    placeholder="画像URLを貼り付け"
                    className="flex-1 px-3 py-2 rounded-md border border-background-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-950 bg-background-50"
                  />
                  <button
                    onClick={() => handleApplyUrl(f.key)}
                    disabled={!inputValue.trim()}
                    className="px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    適用
                  </button>
                </div>
              </div>
            );
          })}

          {hasCustom() && (
            <button
              onClick={resetAll}
              className="w-full py-2.5 rounded-full border border-primary-300 text-primary-700 hover:bg-primary-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              すべてデフォルトに戻す
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  const { images, updateImage, resetAll, hasCustom, loaded } = useEditableImages();
  const [panelOpen, setPanelOpen] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mql.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const cards = [
    {
      img: images.card1,
      title: "車内広々の無料シャトル",
      sub: "駐車場 ⇄ 球場 5分",
      icon: "ri-bus-2-line",
      offset: "",
    },
    {
      img: images.card2,
      title: "100台以上余裕の駐車スペース",
      sub: "車種問わず一律料金",
      icon: "ri-parking-line",
      offset: "",
    },
    {
      img: images.card3,
      title: "駐車管理スタッフ",
      sub: "大切なお車を定期的に見回りします。鍵はお預かりしません。",
      icon: "ri-user-star-line",
      offset: "",
    },
  ];

  return (
    <>
    <section id="top" className="relative min-h-[300px] md:min-h-[820px] w-full">
      {reducedMotion ? (
        <img
          src={images.heroBg}
          alt="エスコンフィールド北海道 送迎付き駐車場アネットパーキング"
          className="absolute top-0 left-0 right-0 h-[300px] md:inset-0 md:w-full md:h-full w-full object-cover object-top"
        />
      ) : (
        <AnimImageZoomRotate
          zoom={1.08}
          rotate={2}
          className="absolute top-0 left-0 right-0 h-[300px] md:inset-0 md:w-full md:h-full w-full overflow-hidden"
        >
          <img
            src={images.heroBg}
            alt="エスコンフィールド北海道 送迎付き駐車場アネットパーキング"
            className="w-full h-full object-cover object-top"
          />
        </AnimImageZoomRotate>
      )}

      <div className="relative z-10 h-full w-full flex flex-col">
        <div className="flex-1 flex items-start pt-4 md:pt-6">
          <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-10 text-center reveal">
            <span className="inline-flex items-center gap-2 bg-foreground-950/55 backdrop-blur-md border border-background-50/30 text-background-50 rounded-full px-3 py-1 md:px-4 md:py-1.5 text-[11px] md:text-sm font-medium whitespace-nowrap">
              <span className="w-2 h-2 flex items-center justify-center">
                <span className="w-2 h-2 rounded-full bg-accent-400 md:animate-pulse" />
              </span>
              北広島市 エスコンフィールド北海道 専用
            </span>
          </div>
        </div>

      </div>

      {/* Mobile sticky reserve button */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-3 bg-gradient-to-t from-black/60 to-transparent md:hidden">
        <a
          href="#reserve"
          className="flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors shadow-lg"
        >
          <i className="ri-calendar-check-line text-lg" />
          今すぐ駐車場を予約する
          <i className="ri-arrow-right-line" />
        </a>
      </div>

      <ImageEditPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        images={images}
        updateImage={updateImage}
        resetAll={resetAll}
        hasCustom={hasCustom}
      />
    </section>

    <section className="bg-background-50 px-4 sm:px-6 md:px-10 py-8 md:py-12">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5 items-start">
        {cards.map((c) => {
          const cardContent = (
            <div key={c.title} className={`bg-background-50 rounded-2xl overflow-hidden border border-background-200 ${c.offset}`}>
              <div className="relative h-32 sm:h-40 md:h-40">
                <img
                  src={c.img}
                  alt={c.title}
                  className="absolute inset-0 w-full h-full object-contain"
                />
              </div>
              <div className="p-3 sm:p-4 md:p-5 flex items-center justify-between">
                <div>
                  <div className="text-foreground-950 font-heading text-base md:text-lg">
                    {c.title}
                  </div>
                  <div className="text-foreground-600 text-[11px] md:text-xs mt-0.5 md:mt-1">
                    {c.sub}
                  </div>
                </div>
                <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                  <i className={c.icon} />
                </span>
              </div>
            </div>
          );

          return reducedMotion || isMobile ? (
            cardContent
          ) : (
            <AnimTiltedCard key={c.title} maxTilt={12} strength={0.08}>
              {cardContent}
            </AnimTiltedCard>
          );
        })}
      </div>

      <div className="mt-5 md:mt-7 flex justify-center">
        <a
          href="#reserve"
          className="inline-flex items-center gap-2 md:gap-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full pl-4 md:pl-6 pr-1.5 md:pr-2 py-2 md:py-2.5 text-sm md:text-base font-medium whitespace-nowrap cursor-pointer transition-colors"
        >
          今すぐ駐車場を予約する
          <span className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-background-50 text-primary-600 shrink-0">
            <i className="ri-arrow-right-line" />
          </span>
        </a>
      </div>
    </section>
    </>
  );
}